import { List, difference } from "lodash";

/* eslint-disable @typescript-eslint/no-explicit-any */
import queryString from "query-string";
import { urlList } from "../@types";

/**
 *  Simple server side rendering check
 * @params {void}
 * @returns {boolean}
 */
export const isSSR = () => {
  return typeof window === "undefined";
};

export type DeepPartial<T> = {
  [P in keyof T]?: T[P] extends object ? DeepPartial<T[P]> : T[P];
};

/**
 *
 * @param URLS
 * @param name
 * @param idx
 * @param params
 * @returns {string}
 */
export const getURLS = (
  URLS: urlList,
  name: string,
  idx?: string,
  params?: { [key: string]: string },
) => {
  if (URLS[name]) {
    let route = URLS[name];

    if (idx) {
      route = route.replace(":idx", idx);
    }
    if (params) {
      route += queryString.stringify(params);
    }

    return route;
  } else {
    throw new Error(`URL with the name "${name}" is not defined.`);
  }
};

export function convertHexToRgbA(hex: string, a: number) {
  if (hex.length < 7) throw new Error("Hex should be six in length");

  // Convert the first 2 characters to hexadecimal
  const r = parseInt(hex.substring(1, 3), 16),
    // Convert the middle 2 characters to hexadecimal
    g = parseInt(hex.substring(3, 5), 16),
    // Convert the last 2 characters to hexadecimal
    b = parseInt(hex.substring(5, 7), 16);

  // append them all
  return "rgba(" + r + ", " + g + ", " + b + ", " + a + ")";
}

export function isObject(item: unknown) {
  return item && typeof item === "object" && !Array.isArray(item);
}

export function isArray(item: unknown) {
  return item && Array.isArray(item);
}

export function mergeDeep(
  target: {
    [key: string]: any;
  },
  source: {
    [key: string]: any;
  },
) {
  if (!isObject(target) || !isObject(source))
    throw Error("Both arguments must be objects");

  const output = Object.assign({}, target);
  if (isObject(target) && isObject(source)) {
    Object.keys(source).forEach((key) => {
      if (isObject(source[key])) {
        if (!(key in target)) Object.assign(output, { [key]: source[key] });
        else output[key] = mergeDeep(target[key], source[key]);
      } else if (isArray(source[key])) {
        if (!(key in target)) {
          Object.assign(output, { [key]: source[key] });
        } else {
          output[key] = [...target[key], ...source[key]];
        }
      } else {
        Object.assign(output, { [key]: source[key] });
      }
    });
  }
  return output;
}

export function getObjectDifferenceValue<D>(
  obj: Partial<D>,
  objectToCompare: Partial<D>,
): Partial<D> {
  const newObj: Partial<D> = {};
  for (const key in obj) {
    if (obj[key] === objectToCompare[key]) {
      continue;
    }

    newObj[key] = obj[key];
  }

  return newObj;
}

export function getObjectDifferenceValueWithKey<D>(
  obj: Partial<D> | null | undefined,
  objectToCompare: Partial<D> | null | undefined,
): Partial<D> {
  const newObj: any = {};

  if (obj === objectToCompare) {
    return newObj;
  }

  if ((obj === null || obj === undefined) && objectToCompare) {
    return objectToCompare;
  }
  if ((objectToCompare === null || objectToCompare === undefined) && obj) {
    return obj;
  }

  for (const key in obj) {
    if (
      typeof obj[key] === "object" &&
      !Array.isArray(obj[key]) &&
      obj[key] !== null
    ) {
      if (!objectToCompare || !objectToCompare[key]) {
        newObj[key] = obj[key];
        continue;
      }
      const diffObj = getObjectDifferenceValueWithKey(
        obj[key] as Partial<D>,
        objectToCompare[key] as Partial<D>,
      );

      if (Object.keys(diffObj).length > 0) {
        newObj[key] = diffObj as any;
      }
      continue;
    }
    if (Array.isArray(obj[key])) {
      const diffArr = difference(
        obj[key] as List<unknown>,
        ((objectToCompare && objectToCompare[key]) as List<unknown>) || [],
      );
      if (diffArr.length > 0) {
        newObj[key] = diffArr as any;
      }
      continue;
    }
    if (obj[key] === (objectToCompare && objectToCompare[key])) {
      continue;
    }

    newObj[key] = obj[key];
  }
  return newObj;
}
